<template>
  <div class="users-page py-3">
    <header class="users-toolbar">
      <div class="users-toolbar__title">
        <span class="users-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Users</h1>
          <p>Manage team members across organisations and roles</p>
        </div>
      </div>
      <div class="users-toolbar__actions">
        <span v-if="lastUpdatedAt && !isLoading" class="users-updated">
          Updated {{ lastUpdatedLabel }}
        </span>
        <CButton
          size="sm"
          class="refresh-btn"
          :class="{ 'is-loading': isLoading }"
          :disabled="isLoading"
          @click="loadUsers"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="refresh-btn__icon" aria-hidden="true">
            <polyline points="23 4 23 10 17 10"/>
            <polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
          <span>{{ isLoading ? 'Refreshing…' : 'Refresh' }}</span>
        </CButton>
        <CButton
          color="primary"
          size="sm"
          class="create-user-btn"
          @click="goToCreateUser"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" class="create-user-btn__icon" aria-hidden="true">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          <span>Create User</span>
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="users-alert">{{ errorMessage }}</CAlert>

    <div class="summary-tiles">
      <div
        v-for="card in summaryCards"
        :key="card.key"
        :class="['summary-tile', `summary-tile--${card.key}`]"
      >
        <span class="summary-tile__label">{{ card.label }}</span>
        <span class="summary-tile__value">{{ card.value }}</span>
      </div>
    </div>

    <div class="summary-tiles summary-tiles--telesales">
      <div
        v-for="card in telesalesCards"
        :key="card.key"
        :class="['summary-tile', `summary-tile--${card.key}`]"
      >
        <span class="summary-tile__label">{{ card.label }}</span>
        <span class="summary-tile__value">{{ card.value }}</span>
      </div>
    </div>

    <section class="users-section">
      <header class="users-section__head">
        <h2>All Users</h2>
        <span v-if="!isLoading && filteredUsers.length" class="users-count">
          {{ paginationLabel }}
        </span>
      </header>

      <div class="users-section__body users-filters">
        <div class="users-filters__search">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="users-search-icon" aria-hidden="true">
            <circle cx="11" cy="11" r="7"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            v-model.trim="searchTerm"
            type="text"
            class="users-input"
            placeholder="Search name, email, organisation…"
          />
          <button
            v-if="searchTerm"
            type="button"
            class="users-search-clear"
            aria-label="Clear search"
            @click="searchTerm = ''"
          >×</button>
        </div>

        <div :class="['users-filter', { 'is-active': !!filters.organisation }]">
          <AppDropdown
            :value="filters.organisation"
            :options="organisationOptions"
            placeholder="Organisation"
            searchable
            search-placeholder="Search organisations…"
            @update:value="filters.organisation = $event"
          />
        </div>

        <div :class="['users-filter', { 'is-active': !!filters.department }]">
          <AppDropdown
            :value="filters.department"
            :options="departmentOptions"
            placeholder="Department"
            searchable
            search-placeholder="Search departments…"
            @update:value="filters.department = $event"
          />
        </div>

        <div :class="['users-filter', { 'is-active': filters.stateId !== '' }]">
          <AppDropdown
            :value="filters.stateId"
            :options="stateFilterOptions"
            :placeholder="isLoadingStates ? 'Loading states…' : 'State'"
            searchable
            search-placeholder="Search states…"
            @update:value="filters.stateId = $event"
          />
        </div>

        <div :class="['users-filter', { 'is-active': filters.branchId !== '' }]">
          <AppDropdown
            :value="filters.branchId"
            :options="branchFilterOptions"
            :placeholder="branchFilterPlaceholder"
            searchable
            search-placeholder="Search branches…"
            @update:value="filters.branchId = $event"
          />
        </div>

        <div :class="['users-filter', { 'is-active': !!filters.role }]">
          <AppDropdown
            :value="filters.role"
            :options="roleOptions"
            placeholder="Role"
            searchable
            search-placeholder="Search roles…"
            @update:value="filters.role = $event"
          />
        </div>

        <div :class="['users-filter users-filter--narrow', { 'is-active': !!filters.status }]">
          <AppDropdown
            :value="filters.status"
            :options="statusOptions"
            placeholder="Status"
            @update:value="filters.status = $event"
          />
        </div>

        <div :class="['users-filter', { 'is-active': !!filters.joined }]">
          <AppDropdown
            :value="filters.joined"
            :options="joinedOptions"
            placeholder="Joined"
            @update:value="filters.joined = $event"
          />
        </div>

        <button
          v-if="activeFilterCount"
          type="button"
          class="users-filters__clear"
          @click="clearFilters"
        >
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
          <span>Clear {{ activeFilterCount }}</span>
        </button>
      </div>

      <div v-if="isLoading" class="users-section__body users-loading">
        <CSpinner size="sm" class="mr-2" />
        <span>Loading users…</span>
      </div>

      <div v-else-if="!users.length" class="users-section__body users-empty">
        <div class="users-empty__title">No users yet</div>
        <div class="users-empty__hint">Users you add will appear here.</div>
      </div>

      <div v-else-if="!filteredUsers.length" class="users-section__body users-empty">
        <div class="users-empty__title">No matches for "{{ searchTerm }}"</div>
        <div class="users-empty__hint">
          Try a different name, email, or role —
          <button type="button" class="users-empty__link" @click="searchTerm = ''">clear search</button>.
        </div>
      </div>

      <div v-else>
        <div class="users-table-wrap">
        <table class="users-table">
          <thead>
            <tr>
              <th class="col-id">Id</th>
              <th>User</th>
              <th>Email</th>
              <th>Role</th>
              <th>Organisation</th>
              <th class="col-status">Status</th>
              <th class="col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in paginatedUsers" :key="user.id" class="user-row" @click="showUser(user.id)">
              <td class="col-id">{{ user.id }}</td>

              <td class="col-user">
                <div class="user-cell">
                  <div
                    class="user-avatar"
                    :style="!photoUrl(user) ? { background: avatarColor(user) } : null"
                  >
                    <img
                      v-if="photoUrl(user)"
                      :src="photoUrl(user)"
                      :alt="user.name || 'User photo'"
                      class="user-avatar__img"
                      @error="onAvatarError(user)"
                    />
                    <span v-else>{{ initials(user.name) }}</span>
                  </div>
                  <div>
                    <div class="user-name">{{ user.name }}</div>
                    <div v-if="user.department" class="user-sub">{{ user.department }}</div>
                  </div>
                </div>
              </td>

              <td class="col-email">
                <span v-if="user.email" class="user-email">{{ user.email }}</span>
                <span v-else class="user-sub user-dim">—</span>
              </td>

              <td>
                <span :class="['role-pill', roleClass(user.role)]">{{ formatRole(user.role) }}</span>
              </td>

              <td class="col-org">
                <div class="user-org">{{ user.organisation }}</div>
              </td>

              <td class="col-status">
                <button
                  type="button"
                  :class="['user-status-toggle', { 'user-status-toggle--inactive': isInactive(user), 'is-busy': togglingUserId === user.id }]"
                  :aria-pressed="!isInactive(user)"
                  :aria-label="isInactive(user) ? `Activate ${user.name}` : `Deactivate ${user.name}`"
                  :disabled="togglingUserId === user.id"
                  @click.stop="requestToggle(user)"
                >
                  <span class="user-status-toggle__track">
                    <span class="user-status-toggle__thumb"></span>
                  </span>
                  <span class="user-status-toggle__label">{{ isInactive(user) ? 'Inactive' : 'Active' }}</span>
                </button>
              </td>

              <td class="col-actions">
                <CButton color="primary" size="sm" class="tbl-btn" @click="showUser(user.id)">View</CButton>
              </td>
            </tr>
          </tbody>
        </table>
        </div>

        <div v-if="maxPages > 1" class="users-pagination">
          <CPagination :pages="maxPages" :active-page.sync="activePage" />
        </div>
      </div>
    </section>

    <AppConfirmDialog
      :visible="toggleDialog.visible"
      :title="toggleDialog.title"
      :message="toggleDialog.message"
      :confirm-label="toggleDialog.confirmLabel"
      busy-label="Saving…"
      :busy="toggleDialog.busy"
      :variant="toggleDialog.variant"
      @confirm="confirmToggle"
      @cancel="cancelToggle"
      @update:visible="onToggleDialogVisible"
    />
  </div>
</template>

<script>
import axios from 'axios'
import AppConfirmDialog from '@/components/AppConfirmDialog'
import AppDropdown from '@/components/AppDropdown'
import branchesApi from '@/api/branches.js'
import statesApi from '@/api/states.js'
import {
  ORGANISATION_OPTIONS,
  ROLE_OPTIONS,
  mergeWithUserCache,
  normalizeUsersResponse,
  setUserCache,
  upsertCachedUser
} from './userHelpers'

export default {
  name: 'Users',
  components: { AppConfirmDialog, AppDropdown },
  data () {
    return {
      isLoading: false,
      isLoadingStates: false,
      isLoadingBranches: false,
      errorMessage: '',
      searchTerm: '',
      filters: {
        organisation: '',
        department: '',
        stateId: '',
        branchId: '',
        role: '',
        status: '',
        joined: ''
      },
      users: [],
      states: [],
      branches: [],
      you: null,
      lastUpdatedAt: null,
      activePage: 1,
      perPage: 15,
      brokenAvatars: {},
      togglingUserId: null,
      toggleDialog: {
        visible: false,
        userId: null,
        nextStatus: '',
        title: '',
        message: '',
        confirmLabel: '',
        variant: 'warning',
        busy: false
      }
    }
  },
  computed: {
    organisationOptions () {
      return [{ value: '', label: 'All organisations' }, ...ORGANISATION_OPTIONS.map((name) => ({ value: name, label: name }))]
    },
    departmentOptions () {
      // Departments are per-organisation DB rows and this list spans every org,
      // so a hardcoded option list drifts out of sync with the real names on
      // user records — a mismatch silently filters everyone out. Derive the
      // options from the departments actually present on the loaded users so
      // every option matches the value the filter compares against.
      const present = Array.from(new Set(this.users.map((user) => user.department).filter(Boolean)))
      present.sort((a, b) => {
        if (a === 'None') return 1
        if (b === 'None') return -1
        return a.localeCompare(b)
      })
      return [
        { value: '', label: 'All departments' },
        ...present.map((name) => ({ value: name, label: name === 'None' ? 'No department' : name }))
      ]
    },
    roleOptions () {
      return [{ value: '', label: 'All roles' }, ...ROLE_OPTIONS.map((role) => ({ value: role, label: role === 'None' ? 'No role' : role.replace(/_/g, ' ') }))]
    },
    statusOptions () {
      return [
        { value: '', label: 'All statuses' },
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' }
      ]
    },
    stateFilterOptions () {
      return [
        { value: '', label: 'All states' },
        ...this.states.map((s) => ({ value: s.id, label: s.name }))
      ]
    },
    branchFilterOptions () {
      const list = this.filters.stateId !== ''
        ? this.branches.filter((b) => Number(b.state_id) === Number(this.filters.stateId))
        : this.branches
      return [
        { value: '', label: 'All branches' },
        ...list.map((b) => ({ value: b.id, label: b.name }))
      ]
    },
    branchFilterPlaceholder () {
      if (this.isLoadingBranches) return 'Loading branches…'
      if (this.filters.stateId !== '' && this.branchFilterOptions.length === 1) return 'No branches in state'
      return 'Branch'
    },
    branchById () {
      const map = new Map()
      for (const branch of this.branches) {
        map.set(Number(branch.id), branch)
      }
      return map
    },
    joinedOptions () {
      return [
        { value: '', label: 'Any joined date' },
        { value: '7', label: 'Joined in last 7 days' },
        { value: '30', label: 'Joined in last 30 days' },
        { value: '90', label: 'Joined in last 90 days' },
        { value: 'year', label: 'Joined this year' }
      ]
    },
    activeFilterCount () {
      return Object.entries(this.filters).filter(([, value]) => value !== '' && value != null).length
    },
    filteredUsers () {
      const term = this.searchTerm.toLowerCase()
      const { organisation, department, stateId, branchId, role, status, joined } = this.filters
      const joinedCutoff = this.joinedCutoffMs(joined)

      return this.users.filter((user) => {
        if (organisation && user.organisation !== organisation) return false
        if (department && user.department !== department) return false
        if (role && String(user.role) !== role) return false
        if (status && String(user.status || '').toLowerCase() !== status) return false
        if (branchId !== '' && Number(user.branch_id) !== Number(branchId)) return false
        if (stateId !== '') {
          const branch = user.branch_id != null ? this.branchById.get(Number(user.branch_id)) : null
          if (!branch || Number(branch.state_id) !== Number(stateId)) return false
        }
        if (joinedCutoff !== null) {
          const joinedMs = this.userJoinedMs(user)
          if (joinedMs === null || joinedMs < joinedCutoff) return false
        }

        if (!term) return true

        return [user.id, user.name, user.email, user.organisation, user.role, user.department, user.status]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term))
      })
    },
    summaryCards () {
      const activeUsers = this.users.filter((user) => String(user.status).toLowerCase() === 'active').length
      const organisations = new Set(this.users.map((user) => user.organisation).filter(Boolean))
      const roles = new Set(this.users.map((user) => user.role).filter((role) => role && role !== 'None'))

      return [
        { key: 'total', label: 'Total Users', value: this.users.length },
        { key: 'active', label: 'Active Users', value: activeUsers },
        { key: 'orgs', label: 'Organisations', value: organisations.size },
        { key: 'roles', label: 'Roles Seen', value: roles.size }
      ]
    },
    telesalesCards () {
      const isTelesales = (user) => String(user.role).toLowerCase() === 'telesales'
      const telesalesInOrg = (orgName) => this.users.filter((user) => isTelesales(user) && user.organisation === orgName).length

      return [
        { key: 'map', label: 'MAP Telesales', value: telesalesInOrg('Manakkal Agro Pharma') },
        { key: 'mdi', label: 'MDI Telesales', value: telesalesInOrg('Manakkal Drugs India') },
        { key: 'mvp', label: 'MVP Telesales', value: telesalesInOrg('Manakkal Veterinary Pharma') }
      ]
    },
    lastUpdatedLabel () {
      if (!this.lastUpdatedAt) return ''
      const hours = String(this.lastUpdatedAt.getHours()).padStart(2, '0')
      const minutes = String(this.lastUpdatedAt.getMinutes()).padStart(2, '0')
      return `${hours}:${minutes}`
    },
    maxPages () {
      return Math.max(1, Math.ceil(this.filteredUsers.length / this.perPage))
    },
    paginatedUsers () {
      const start = (this.activePage - 1) * this.perPage
      return this.filteredUsers.slice(start, start + this.perPage)
    },
    paginationLabel () {
      const total = this.filteredUsers.length
      if (!total) return ''
      const start = (this.activePage - 1) * this.perPage + 1
      const end = Math.min(start + this.perPage - 1, total)
      const noun = total === 1 ? 'user' : 'users'
      if (total <= this.perPage) {
        return `${total} of ${this.users.length} ${noun}`
      }
      return `${start}–${end} of ${total} ${noun}`
    }
  },
  watch: {
    searchTerm () {
      this.activePage = 1
    },
    filters: {
      deep: true,
      handler () {
        this.activePage = 1
      }
    },
    'filters.stateId' (newId) {
      if (newId === '' || this.filters.branchId === '') return
      const branch = this.branchById.get(Number(this.filters.branchId))
      if (!branch || Number(branch.state_id) !== Number(newId)) {
        this.filters.branchId = ''
      }
    },
    maxPages (value) {
      if (this.activePage > value) {
        this.activePage = value
      }
    }
  },
  created () {
    this.loadUsers()
    this.loadStates()
    this.loadBranches()
  },
  methods: {
    showUser (id) {
      this.$router.push({ path: `/users/${id}` })
    },
    goToCreateUser () {
      this.$router.push({ name: 'CreateNewUser' })
    },
    loadUsers () {
      this.isLoading = true
      this.errorMessage = ''

      axios.get(`${this.$apiAdress}/api/users?token=${localStorage.getItem('api_token')}`)
        .then((response) => {
          const normalized = normalizeUsersResponse(response.data)
          const rows = mergeWithUserCache(normalized.users)
          this.users = rows
          setUserCache(rows)
          this.you = normalized.you
          this.lastUpdatedAt = new Date()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = error?.response?.data?.message || 'Unable to load the users list right now.'
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    loadStates () {
      this.isLoadingStates = true
      statesApi.list(this.$apiAdress)
        .then((data) => {
          this.states = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : [])
        })
        .catch((error) => { console.log(error) })
        .finally(() => { this.isLoadingStates = false })
    },
    loadBranches () {
      this.isLoadingBranches = true
      branchesApi.list(this.$apiAdress)
        .then((data) => {
          this.branches = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : [])
        })
        .catch((error) => { console.log(error) })
        .finally(() => { this.isLoadingBranches = false })
    },
    initials (name) {
      if (!name) return '?'
      const parts = String(name).trim().split(/\s+/)
      const first = parts[0] ? parts[0][0] : ''
      const second = parts.length > 1 ? parts[parts.length - 1][0] : ''
      return (first + second).toUpperCase() || '?'
    },
    photoUrl (user) {
      if (!user || this.brokenAvatars[user.id]) return ''
      const candidates = [
        user.profile_photo_url,
        user.profile_photo,
        user.image,
        user.avatar_url,
        user.avatar,
        user.photo_url,
        user.photo
      ]
      for (const candidate of candidates) {
        if (candidate && typeof candidate === 'string') return candidate
      }
      return ''
    },
    onAvatarError (user) {
      if (!user || user.id == null) return
      this.$set(this.brokenAvatars, user.id, true)
    },
    avatarColor (user) {
      const palette = [
        'linear-gradient(135deg, #0e5b46 0%, #16a07a 100%)',
        'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        'linear-gradient(135deg, #6d28d9 0%, #a855f7 100%)',
        'linear-gradient(135deg, #b45309 0%, #f59e0b 100%)',
        'linear-gradient(135deg, #be185d 0%, #ec4899 100%)',
        'linear-gradient(135deg, #115e59 0%, #14b8a6 100%)'
      ]
      const seed = String(user?.name || user?.id || '')
      let hash = 0
      for (let i = 0; i < seed.length; i++) {
        hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
      }
      return palette[hash % palette.length]
    },
    isInactive (user) {
      return String(user?.status || '').toLowerCase() === 'inactive'
    },
    clearFilters () {
      this.filters = { organisation: '', department: '', stateId: '', branchId: '', role: '', status: '', joined: '' }
    },
    joinedCutoffMs (value) {
      if (!value) return null
      const now = new Date()
      if (value === 'year') {
        return new Date(now.getFullYear(), 0, 1).getTime()
      }
      const days = Number(value)
      if (!Number.isFinite(days) || days <= 0) return null
      return now.getTime() - days * 24 * 60 * 60 * 1000
    },
    userJoinedMs (user) {
      const raw = user?.date_of_joining || user?.created_at || user?.joined_at
      if (!raw) return null
      const ts = new Date(raw).getTime()
      return Number.isFinite(ts) ? ts : null
    },
    requestToggle (user) {
      if (!user || this.togglingUserId) return
      const willBeInactive = !this.isInactive(user)
      this.toggleDialog = {
        visible: true,
        userId: user.id,
        nextStatus: willBeInactive ? 'inactive' : 'active',
        title: willBeInactive ? `Set ${user.name} as inactive?` : `Reactivate ${user.name}?`,
        message: willBeInactive
          ? 'They will not be able to sign in. Their records, orders, and history remain intact.'
          : 'They will be able to sign in again with their existing credentials.',
        confirmLabel: willBeInactive ? 'Set inactive' : 'Reactivate',
        variant: willBeInactive ? 'warning' : 'info',
        busy: false
      }
    },
    cancelToggle () {
      if (this.toggleDialog.busy) return
      this.closeToggleDialog()
    },
    onToggleDialogVisible (value) {
      if (!value && !this.toggleDialog.busy) this.closeToggleDialog()
    },
    closeToggleDialog () {
      this.toggleDialog = { ...this.toggleDialog, visible: false, busy: false }
    },
    confirmToggle () {
      const { userId, nextStatus } = this.toggleDialog
      const user = this.users.find((u) => u.id === userId)
      if (!user) {
        this.closeToggleDialog()
        return
      }

      this.toggleDialog.busy = true
      this.togglingUserId = userId

      axios.post(
        `${this.$apiAdress}/api/users/${userId}?token=${localStorage.getItem('api_token')}`,
        { employment_status: nextStatus }
      )
        .then(() => {
          const nextLabel = nextStatus === 'inactive' ? 'Inactive' : 'Active'
          const idx = this.users.findIndex((u) => u.id === userId)
          if (idx !== -1) this.$set(this.users, idx, { ...this.users[idx], status: nextLabel, employment_status: nextStatus })
          upsertCachedUser(userId, { status: nextLabel, employment_status: nextStatus })
          this.closeToggleDialog()
        })
        .catch((error) => {
          console.log(error)
          const data = error?.response?.data
          const firstFieldError = data?.errors ? Object.values(data.errors)[0] : null
          this.errorMessage = (Array.isArray(firstFieldError) ? firstFieldError[0] : firstFieldError) ||
            data?.message || 'Unable to update the user status right now.'
          this.toggleDialog.busy = false
        })
        .finally(() => {
          this.togglingUserId = null
        })
    },
    formatRole (role) {
      if (!role || role === 'None') return 'No role'
      return String(role).replace(/_/g, ' ')
    },
    roleClass (role) {
      const r = String(role || '').toLowerCase()
      if (!r || r === 'none') return 'role-pill--gray'
      if (r === 'superadmin' || r === 'admin') return 'role-pill--violet'
      if (r === 'telesales' || r === 'crm' || r === 'agent') return 'role-pill--blue'
      if (r === 'packer' || r === 'storekeeper' || r === 'field_staff') return 'role-pill--amber'
      if (r === 'accountant' || r === 'junior_accountant') return 'role-pill--teal'
      if (r === 'moderator' || r === 'sales_supervisor' || r === 'sales_admin' || r === 'ecom_manager' || r === 'marketing') return 'role-pill--green'
      return 'role-pill--gray'
    }
  }
}
</script>

<style scoped>
.users-page {
  padding: 0.25rem 0;
}

/* ── Toolbar ── */
.users-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.users-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.users-toolbar__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.users-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.users-toolbar__title p {
  margin: 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.users-toolbar__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.65rem;
}

.users-updated {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.users-toolbar__actions >>> .btn.create-user-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  background: var(--brand-gradient, linear-gradient(135deg, #0e5b46 0%, #16a07a 100%));
  border: 0;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.users-toolbar__actions >>> .btn.create-user-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
  color: #ffffff;
}

.users-toolbar__actions >>> .btn.create-user-btn:active {
  transform: translateY(0);
  filter: brightness(0.96);
}

.create-user-btn__icon {
  flex-shrink: 0;
  display: block;
}

.users-toolbar__actions >>> .btn.refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.85rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.users-toolbar__actions >>> .btn.refresh-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.users-toolbar__actions >>> .btn.refresh-btn:disabled {
  opacity: 1;
  background: #f8fafc;
  color: #4b5563;
  cursor: progress;
}

.refresh-btn__icon {
  flex-shrink: 0;
  display: block;
  color: #16a07a;
  transition: transform 0.4s ease;
}

.users-toolbar__actions >>> .btn.refresh-btn:hover:not(:disabled) .refresh-btn__icon {
  transform: rotate(60deg);
}

.users-toolbar__actions >>> .btn.refresh-btn.is-loading .refresh-btn__icon {
  animation: refresh-spin 0.9s linear infinite;
  color: #16a07a;
}

@keyframes refresh-spin {
  to { transform: rotate(360deg); }
}

/* ── Alert ── */
.users-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

/* ── Summary tiles ── */
.summary-tiles {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.summary-tiles--telesales {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.summary-tile {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  border-left-width: 4px;
}

.summary-tile--total  { border-left-color: #1f2a44; }
.summary-tile--active { border-left-color: #16a07a; }
.summary-tile--orgs   { border-left-color: #6d28d9; }
.summary-tile--roles  { border-left-color: #0ea5e9; }
.summary-tile--map    { border-left-color: #0e5b46; }
.summary-tile--mdi    { border-left-color: #2563eb; }
.summary-tile--mvp    { border-left-color: #d97706; }

.summary-tile__label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #4b5563;
}

.summary-tile__value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2a44;
}

/* ── Section card ── */
.users-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
}

.users-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
  border-radius: 12px 12px 0 0;
}

.users-section__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.users-count {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  background: #f1f5f9;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
}

.users-section__body {
  padding: 1rem;
}

/* ── Filters ── */
.users-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #fbfcfe;
  border-bottom: 1px solid #eef1f7;
}

.users-filters__search {
  position: relative;
  flex: 1 1 280px;
  min-width: 220px;
}

.users-search-icon {
  position: absolute;
  top: 50%;
  left: 0.7rem;
  transform: translateY(-50%);
  color: #6b7280;
  pointer-events: none;
}

.users-input {
  width: 100%;
  height: 34px;
  padding: 0 2rem 0 2rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.82rem;
  color: #2f3640;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.users-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.users-search-clear {
  position: absolute;
  top: 50%;
  right: 0.4rem;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border: 0;
  border-radius: 50%;
  background: #e5e9f2;
  color: #4b5563;
  font-size: 0.95rem;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, color 0.15s ease;
}

.users-search-clear:hover {
  background: #d1d5db;
  color: #1f2a44;
}

.users-filter {
  flex: 0 1 160px;
  min-width: 130px;
}

.users-filter--narrow {
  flex: 0 1 130px;
  min-width: 110px;
}

.users-filter >>> .app-dropdown__trigger {
  height: 34px;
  font-size: 0.82rem;
  border-radius: 8px;
}

.users-filter.is-active >>> .app-dropdown__trigger {
  border-color: #16a07a;
  background: #f5fbf8;
  color: #0e5b46;
  font-weight: 600;
}

.users-filter.is-active >>> .app-dropdown__caret {
  color: #0e5b46;
}

.users-filters__clear {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  height: 34px;
  padding: 0 0.6rem;
  margin-left: auto;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #6b7280;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease;
}

.users-filters__clear:focus { outline: none; }
.users-filters__clear::-moz-focus-inner { border: 0; }
.users-filters__clear:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.users-filters__clear:hover {
  color: #b91c1c;
  background: #fef2f2;
}

/* ── Loading / empty ── */
.users-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 3rem 1rem;
  color: #6b7280;
  font-size: 0.85rem;
}

.users-empty {
  text-align: center;
  padding: 3rem 1rem;
}

.users-empty__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  margin-bottom: 0.25rem;
}

.users-empty__hint {
  font-size: 0.82rem;
  color: #6b7280;
}

.users-empty__link {
  border: 0;
  background: transparent;
  padding: 0;
  color: #0e5b46;
  font-weight: 600;
  cursor: pointer;
}

.users-empty__link:hover {
  text-decoration: underline;
}

/* ── Table ── */
.users-table-wrap {
  overflow-x: auto;
}

.users-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.82rem;
}

.users-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #fbfcfe;
  border-top: 0;
  border-bottom: 2px solid #e5e9f2;
  padding: 0.65rem 0.9rem;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #374151;
  white-space: nowrap;
  text-align: left;
}

.users-table tbody tr {
  cursor: pointer;
  transition: background-color 0.12s ease;
}

.users-table tbody tr:hover {
  background: #f8fafc;
}

.users-table tbody tr:hover .col-id {
  border-left-color: #16a07a;
}

.users-table td {
  padding: 0.85rem 0.9rem;
  border-bottom: 1px solid #eef1f7;
  vertical-align: middle;
  color: #374151;
}

.col-id      { width: 64px; color: #6b7280; font-weight: 600; border-left: 3px solid transparent; transition: border-color 0.15s ease; padding-left: 0.75rem; }
.col-user    { min-width: 220px; }
.col-email   { min-width: 200px; }
.col-org     { min-width: 200px; }
.col-status  { width: 130px; white-space: nowrap; }
.col-actions { width: 90px; text-align: right; white-space: nowrap; }

/* ── User cell ── */
.user-cell {
  display: flex;
  align-items: center;
  gap: 0.7rem;
}

.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-weight: 700;
  font-size: 0.78rem;
  letter-spacing: 0.02em;
  flex-shrink: 0;
  overflow: hidden;
  background: #e5e9f2;
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.12);
}

.user-avatar__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.user-name {
  font-size: 0.88rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.25;
}

.user-sub {
  font-size: 0.73rem;
  color: #4b5563;
  line-height: 1.4;
}

.user-dim {
  color: #6b7280;
}

.user-email {
  font-size: 0.82rem;
  color: #374151;
  word-break: break-all;
}

.user-org {
  font-size: 0.85rem;
  font-weight: 600;
  color: #1f2a44;
  margin-bottom: 0.25rem;
}

/* ── Role pill ── */
.role-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.18rem 0.6rem;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: capitalize;
  white-space: nowrap;
}

.role-pill--gray   { background: #f1f5f9; color: #475569; }
.role-pill--violet { background: #ede9fe; color: #5b21b6; }
.role-pill--blue   { background: #dbeafe; color: #1e40af; }
.role-pill--amber  { background: #fef3c7; color: #92400e; }
.role-pill--teal   { background: #ccfbf1; color: #115e59; }
.role-pill--green  { background: #dff5ea; color: #0e5b46; }

/* ── Status toggle ── */
.user-status-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  color: #1f2a44;
  line-height: 1;
}

.user-status-toggle:focus { outline: none; }
.user-status-toggle::-moz-focus-inner { border: 0; }
.user-status-toggle:focus-visible .user-status-toggle__track {
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.18), 0 0 0 3px rgba(22, 160, 122, 0.22);
}
.user-status-toggle--inactive:focus-visible .user-status-toggle__track {
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.18), 0 0 0 3px rgba(220, 38, 38, 0.28);
}

.user-status-toggle__track {
  position: relative;
  width: 34px;
  height: 19px;
  border-radius: 999px;
  background: var(--brand-gradient, linear-gradient(135deg, #0e5b46 0%, #16a07a 100%));
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.18);
  transition: background 0.18s ease, box-shadow 0.15s ease;
  flex-shrink: 0;
}

.user-status-toggle__thumb {
  position: absolute;
  top: 2px;
  left: 17px;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.24);
  transition: left 0.18s ease;
}

.user-status-toggle--inactive .user-status-toggle__track {
  background: #d1d5db;
}

.user-status-toggle--inactive .user-status-toggle__thumb {
  left: 2px;
}

.user-status-toggle__label {
  font-size: 0.76rem;
  font-weight: 600;
  color: #0e5b46;
  letter-spacing: 0.01em;
}

.user-status-toggle--inactive .user-status-toggle__label {
  color: #b91c1c;
}

.user-status-toggle:disabled,
.user-status-toggle.is-busy {
  cursor: progress;
  opacity: 0.7;
}

/* ── Action button ── */
.tbl-btn {
  border-radius: 7px !important;
  font-weight: 600;
  font-size: 0.78rem;
}

/* ── Pagination ── */
.users-pagination {
  display: flex;
  justify-content: flex-end;
  padding: 0.75rem 1rem;
  border-top: 1px solid #eef1f7;
}

.users-pagination >>> .pagination {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.users-pagination >>> .page-item {
  margin: 0;
}

.users-pagination >>> .page-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 0.6rem;
  border: 1px solid #e5e9f2;
  border-radius: 8px;
  background: #ffffff;
  color: #4b5563;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1;
  text-decoration: none;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.users-pagination >>> .page-link:hover {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  text-decoration: none;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.users-pagination >>> .page-link:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(14, 91, 70, 0.22);
}

.users-pagination >>> .page-item.active .page-link {
  background: var(--brand-gradient, linear-gradient(135deg, #0e5b46 0%, #16a07a 100%));
  border-color: transparent;
  color: #ffffff;
  box-shadow: var(--brand-shadow, 0 4px 10px rgba(14, 91, 70, 0.28));
}

.users-pagination >>> .page-item.active .page-link:hover {
  color: #ffffff;
  filter: brightness(1.05);
}

.users-pagination >>> .page-item.disabled .page-link {
  background: #fbfcfe;
  border-color: #eef1f7;
  color: #9ca3af;
  cursor: not-allowed;
  pointer-events: none;
  box-shadow: none;
}

/* ── Responsive ── */
@media (max-width: 991px) {
  .summary-tiles {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .users-toolbar {
    flex-wrap: wrap;
    align-items: flex-start;
  }

  .users-toolbar__title {
    flex: 1 1 100%;
  }

  .users-toolbar__actions {
    flex: 1 1 100%;
    justify-content: flex-start;
  }

  .summary-tiles {
    grid-template-columns: 1fr 1fr;
  }

  .users-filters__clear {
    margin-left: 0;
  }
}

@media (max-width: 575px) {
  .users-page {
    padding: 0 0 0.5rem;
  }
  .users-toolbar {
    padding: 0.25rem 0 0.75rem;
    gap: 0.5rem;
  }
  .users-toolbar__accent {
    height: 30px;
  }
  .users-toolbar__title h1 {
    font-size: 1rem;
  }
  .users-toolbar__title p {
    font-size: 0.72rem;
  }
  .users-toolbar__actions >>> .btn {
    height: 32px;
    font-size: 0.76rem;
    padding: 0 0.7rem;
  }
  .summary-tiles {
    grid-template-columns: 1fr;
  }
}
</style>
